import { useState, useEffect, useCallback, useRef } from "react";
import { getOrCreateDirectRoom, sendMessage, getMessages, ChatMessage as WsChatMessage } from "../api/services/chat";
import type { GinoMessageMeta } from "../types/chatPersistence";

const GINO_USER_ID = Number(process.env.REACT_APP_GINO_USER_ID);

interface QueuedMessage {
  text: string;
  role: "user" | "gino";
  meta?: Partial<GinoMessageMeta>;
}

export const useGinoChatRoom = (initialChatId?: number | string | null) => {
  // When an existing room id is supplied (resuming a past conversation from
  // history) bind directly to it. Otherwise mint a fresh room — the GINO user
  // is special-cased on the backend so get-or-create always creates a new room.
  const boundId =
    initialChatId != null && String(initialChatId).trim() !== "" && !isNaN(Number(initialChatId))
      ? Number(initialChatId)
      : null;

  const [chatId, setChatId] = useState<number | null>(null);
  const [isRoomReady, setIsRoomReady] = useState(false);
  const queueRef = useRef<QueuedMessage[]>([]);
  // Guards against overlapping flushes so concurrent persists can never
  // interleave their POSTs — the server stamps created_at/id in receipt order,
  // so out-of-order sends reshuffle the transcript on resume.
  const isFlushingRef = useRef(false);
  const [flushTrigger, setFlushTrigger] = useState(0);

  useEffect(() => {
    // RESUME: bind to the existing room synchronously, no network call.
    if (boundId !== null) {
      setChatId(boundId);
      setIsRoomReady(true);
      return;
    }
    // FRESH: create a new GINO room for this conversation.
    const init = async () => {
      try {
        const room = await getOrCreateDirectRoom({ otherUserId: GINO_USER_ID });
        setChatId(room.chatId);
        setIsRoomReady(true);
      } catch (err) {
        console.error("Failed to create/get GINO chat room:", err);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundId]);

  // Drain the queue strictly in FIFO order, one POST at a time. ALL persists go
  // through this single path (see persistMessage) so card/narration ordering is
  // deterministic on resume. An in-flight flush no-ops re-entrant triggers, then
  // re-checks the queue on completion so messages enqueued mid-flush are sent.
  useEffect(() => {
    if (!chatId || !isRoomReady) return;
    if (isFlushingRef.current) return;
    if (queueRef.current.length === 0) return;

    const flush = async () => {
      isFlushingRef.current = true;
      try {
        while (queueRef.current.length > 0) {
          const msg = queueRef.current.shift()!;
          try {
            await sendMessage(chatId, {
              room: chatId,
              type: "text",
              text: msg.text,
              meta: msg.meta
                ? JSON.stringify({ role: msg.role, ...msg.meta })
                : JSON.stringify({ role: msg.role, messageType: "text" }),
              isGino: true,
            });
          } catch (err) {
            console.error("Failed to flush queued GINO message:", err);
          }
        }
      } finally {
        isFlushingRef.current = false;
      }
    };
    flush();
  }, [chatId, isRoomReady, flushTrigger]);

  // Always enqueue — never send directly. A direct send would race the queued
  // flushes (and other concurrent persists), and the server stamps created_at/id
  // in receipt order, so out-of-order sends reshuffle the transcript on resume.
  const persistMessage = useCallback(async (text: string, role: "user" | "gino", meta?: Partial<GinoMessageMeta>) => {
    queueRef.current.push({ text, role, meta });
    setFlushTrigger(prev => prev + 1);
  }, []);

  const loadHistory = useCallback(async (): Promise<WsChatMessage[]> => {
    if (!chatId) return [];
    try {
      return await getMessages(chatId);
    } catch (err) {
      console.error("Failed to load GINO chat history:", err);
      return [];
    }
  }, [chatId]);

  return { chatId, isRoomReady, persistMessage, loadHistory };
};
